import { useState } from "react";
import { Link2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Inline search + link when the client isn't bound to an SbN record yet.
export function SbnLinkSearch({ email, clientName, onLinked }) {
  const [q, setQ] = useState(() => (clientName || "").trim().split(/\s+/).pop() || "");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  async function search(e) {
    e?.preventDefault();
    if (q.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await api.get("/sbn/clients", { params: { q: q.trim(), take: 15 } });
      setRows(r.data?.clients || []);
    } catch (err) {
      toast.error("SbN search failed", { description: err?.response?.data?.detail || err.message });
    } finally { setBusy(false); }
  }

  async function link(c) {
    const name = [c.first_name, c.last_name].filter(Boolean).join(" ");
    try {
      await api.post("/sbn/links", { email, sbn_client_id: c.client_id, sbn_client_name: name || null });
      toast.success(`Linked to SbN client ${name || c.client_id}`);
      onLinked();
    } catch (err) {
      toast.error("Link failed", { description: err?.response?.data?.detail || err.message });
    }
  }

  if (!email) return <p className="text-sm text-zinc-500">This assessment has no client email to link to SbN.</p>;
  return (
    <div className="space-y-2" data-testid="sbn-link-search">
      <p className="text-sm text-zinc-600">Not linked to a Strength by Numbers client yet — find them to pull results.</p>
      <form onSubmit={search} className="flex gap-2 max-w-md">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SbN by name" data-testid="sbn-link-search-input" />
        <Button type="submit" size="sm" variant="outline" disabled={busy} data-testid="sbn-link-search-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
        </Button>
      </form>
      {rows && !rows.length && <p className="text-xs text-zinc-500">No SbN clients match “{q}”.</p>}
      {rows?.length > 0 && (
        <ul className="divide-y rounded-md border border-zinc-200 max-w-xl">
          {rows.map((c) => (
            <li key={c.client_id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <div className="min-w-0">
                <div className="font-medium text-zinc-900">{[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}</div>
                <div className="text-xs text-zinc-500 truncate">{c.email || "no email"}{c.lastSession?.time ? ` · last session ${new Date(c.lastSession.time).toLocaleDateString()}` : ""}</div>
              </div>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => link(c)} data-testid={`sbn-link-client-${c.client_id}`}>
                <Link2 className="h-3 w-3 mr-1" /> Link
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
